'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormInputField } from '@/components/forms/form-input-field';
import { SectionLabel } from '@/components/forms/section-label';
import { FormSubmitOverlay } from '@/components/forms/form-submit-overlay';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const formSchema = z.object({
  question: z
    .string()
    .min(5, 'Question must be at least 5 characters')
    .max(1000, 'Question cannot exceed 1000 characters'),
  options: z
    .array(z.string().min(1, 'Option cannot be empty').max(200))
    .min(2, 'At least 2 options are required')
    .max(10, 'Maximum 10 options allowed'),
  isAnonymous: z.boolean(),
  allowMultiple: z.boolean(),
  expiresAt: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        const d = new Date(val.includes('T') ? val : `${val}T23:59:59`);
        if (isNaN(d.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d >= today;
      },
      { message: 'Expiration date cannot be in the past' }
    ),
});

type FormValues = z.infer<typeof formSchema>;

export function PollForm() {
  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [options, setOptions] = useState<string[]>(['', '']);

  const todayStr = new Date().toLocaleDateString('en-CA');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: '',
      options: ['', ''],
      isAnonymous: false,
      allowMultiple: false,
      expiresAt: '',
    },
  });

  const handleAddOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
      form.setValue('options', newOptions);
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
    form.setValue('options', newOptions);
  };

  const onSubmit = async (values: FormValues) => {
    if (!communityId) return;

    const filteredOptions = values.options.filter((opt) => opt.trim() !== '');

    if (filteredOptions.length < 2) {
      toast.error('At least 2 options are required');
      return;
    }

    setIsSubmitting(true);

    try {
      let formattedExpiresAt: string | null = null;
      if (values.expiresAt && values.expiresAt.trim() !== '') {
        const d = new Date(
          values.expiresAt.includes('T') ? values.expiresAt : `${values.expiresAt}T23:59:59`
        );
        if (!isNaN(d.getTime())) {
          formattedExpiresAt = d.toISOString();
        }
      }

      const payload = {
        question: values.question,
        options: filteredOptions,
        isAnonymous: values.isAnonymous,
        allowMultiple: values.allowMultiple,
        expiresAt: formattedExpiresAt,
      };

      const { data } = await axiosInstance.post(`/api/communities/${communityId}/polls`, payload);
      toast.success('Poll created successfully!');
      navigate(`/${communityId}/dashboard?postid=${data.postId}`);
    } catch (error) {
      console.error('Failed to create poll', error);
      toast.error('Failed to create poll. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormSubmitOverlay
        open={isSubmitting}
        title="Creating poll..."
        description="Please keep this screen open while we create your poll."
      />
      <Card className="w-full max-w-5xl mx-auto border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-2xl font-black tracking-tight text-foreground">
            Create Poll
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Ask the community a question and get responses from members.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormTextareaField
                control={form.control}
                name="question"
                label="Poll Question"
                placeholder="What would you like to ask? Use @ for a member or business, # for a category"
                required
                maxLength={1000}
                showCounter
                mentions
                className="min-h-[100px]"
              />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <SectionLabel>Poll Options</SectionLabel>
                  <span className="text-xs text-muted-foreground">Add maximum 10 options</span>
                </div>
                <div className="space-y-2">
                  {options.map((option, index) => (
                    <div key={index} className="flex gap-2 items-end">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className={cn(
                            'w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50',
                            form.formState.errors.options?.[index] && 'border-red-500'
                          )}
                        />
                        {form.formState.errors.options?.[index] && (
                          <p className="text-xs text-red-500 mt-1">
                            {form.formState.errors.options?.[index]?.message}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOption(index)}
                        disabled={options.length <= 2}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {options.length < 10 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddOption}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Option
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card/50">
                  <div className="space-y-0.5">
                    <Label htmlFor="anonymous" className="font-semibold cursor-pointer">
                      Anonymous Voting
                    </Label>
                    <p className="text-xs text-muted-foreground">Voter identities will be hidden</p>
                  </div>
                  <Switch
                    id="anonymous"
                    checked={form.watch('isAnonymous')}
                    onCheckedChange={(checked) => form.setValue('isAnonymous', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card/50">
                  <div className="space-y-0.5">
                    <Label htmlFor="allowMultiple" className="font-semibold cursor-pointer">
                      Allow Multiple Selections
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Members can vote for multiple options
                    </p>
                  </div>
                  <Switch
                    id="allowMultiple"
                    checked={form.watch('allowMultiple')}
                    onCheckedChange={(checked) => form.setValue('allowMultiple', checked)}
                  />
                </div>

                <FormInputField
                  control={form.control}
                  name="expiresAt"
                  label="Expiration Date (Optional)"
                  type="date"
                  min={todayStr}
                  placeholder="Select expiration date"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-11 text-base font-semibold"
                >
                  {isSubmitting ? 'Creating...' : 'Create Poll'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/${communityId}/create`)}
                  className="w-full h-11"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </>
  );
}
